import {
  TouchEventHandler,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { SwipeFiltersContext } from "common/Filters/common/SwipeFilters.context.ts";
import { useRefValue } from "@shared/utils/hooks/misc.ts";
import { round } from "@shared/utils/numbers.ts";

interface UseSwipeDrawerProps {
  isDisabled?: boolean;
  isLeft?: boolean;
  onSwipe?: () => void;
}

const useSwipeDrawer = ({
  isDisabled,
  isLeft,
  onSwipe,
}: UseSwipeDrawerProps) => {
  const [open, setOpen] = useState(false);
  const startXRef = useRef<number | null>(null);
  const { swipeActiveRef } = useContext(SwipeFiltersContext) || {};
  const swipeActivatedRef = useRef(false);
  const refValues = useRefValue({ isLeft, onSwipe });
  const ledgeSize = round(window.innerWidth * 0.1, 0);

  const showDrawer = useCallback(() => {
    setOpen(true);
  }, []);
  const closeDrawer = useCallback(() => {
    setOpen(false);
  }, []);

  const onTouchStart: TouchEventHandler<HTMLDivElement> = useCallback((e) => {
    const touch = e.touches[0];
    startXRef.current = touch.clientX;

    const leftSwipeActive =
      refValues.current.isLeft && touch.clientX < ledgeSize;
    const rightSwipeActive =
      !refValues.current.isLeft &&
      touch.clientX > window.innerWidth - ledgeSize;
    if (swipeActiveRef && (leftSwipeActive || rightSwipeActive)) {
      e.stopPropagation();
      swipeActiveRef.current = true;
    }
  }, []);

  const onTouchMove: TouchEventHandler<HTMLDivElement> = useCallback((e) => {
    const touch = e.touches[0];
    const deltaX = touch.clientX - (startXRef.current || 0);

    const openRight =
      !refValues.current.isLeft &&
      !open &&
      (startXRef.current || 0) > window.innerWidth - ledgeSize &&
      deltaX < -20;
    const openLeft =
      refValues.current.isLeft &&
      (startXRef.current || 0) < ledgeSize &&
      deltaX > 20;
    if ((openRight || openLeft) && !swipeActivatedRef.current) {
      swipeActivatedRef.current = true;
      (refValues.current.onSwipe ?? showDrawer)();
    }
  }, []);

  const onTouchEnd: TouchEventHandler<HTMLDivElement> = useCallback(() => {
    swipeActivatedRef.current = false;
    if (swipeActiveRef) {
      swipeActiveRef.current = false;
    }
  }, []);

  const swipeDrawerProps = isDisabled
    ? undefined
    : {
        onTouchStart,
        onTouchMove,
        onTouchEnd,
      };

  return {
    open,
    closeDrawer,
    swipeDrawerProps,
  };
};

export default useSwipeDrawer;
